// `<SavedIndicator />` — auto-save confirmation chip rendered in the
// editor header next to the screen title (Banks Ask 4 → option a).
// Subscribes to the persistence layer's `lastSavedAt` signal (P-035
// extension to D-083) and renders a relative-time caption.
//
// **Display rules.**
// - `lastSavedAt === null` (no save yet OR last write failed) → renders
//   nothing (the empty caption would compete with the dirty asterisk).
// - Otherwise renders "Saved <relative-time>" — "just now" inside the
//   first 5 s, then "Xs ago", "Xm ago", "Xh ago".
//
// **Tick cadence with back-off.** A self-rescheduling `setTimeout`
// chain refreshes the caption while `lastSavedAt` is non-null. The
// cadence scales with elapsed: 5 s for the first minute, 30 s up to
// one hour, 5 min thereafter. Past an hour the caption only carries
// hour granularity; the slow tick avoids continuous re-renders that
// produce no visible change.

import { useEffect, useState } from 'preact/hooks'

import { useLastSavedAt } from '../../store/persistence'

const ONE_MINUTE_MS = 60_000
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS

const TICK_FAST_MS = 5_000
const TICK_MEDIUM_MS = 30_000
const TICK_SLOW_MS = 5 * ONE_MINUTE_MS

export function SavedIndicator() {
  const lastSavedAt = useLastSavedAt()
  const [, setTick] = useState(0)

  useEffect(() => {
    if (lastSavedAt === null) return undefined
    let timer: ReturnType<typeof setTimeout> | null = null
    const schedule = () => {
      timer = setTimeout(() => {
        setTick((n) => n + 1)
        schedule()
      }, tickInterval(Date.now() - lastSavedAt))
    }
    schedule()
    return () => {
      if (timer !== null) clearTimeout(timer)
    }
  }, [lastSavedAt])

  if (lastSavedAt === null) return null

  const relative = formatRelative(Date.now() - lastSavedAt)
  return (
    <span
      class="text-xs text-gray-500"
      aria-live="polite"
      role="status"
      aria-label={`Last saved ${relative}`}
    >
      Saved {relative}
    </span>
  )
}

/**
 * Format an elapsed duration (ms) as a short relative-time caption.
 * Bands: <5 s = "just now"; <60 s = "Xs ago"; <60 m = "Xm ago";
 * else = "Xh ago".
 */
export function formatRelative(elapsedMs: number): string {
  // Math.max: defends against clock skew (system clock adjustment
  // between the persistence layer's `Date.now()` write and the
  // indicator's render — produces a negative delta otherwise).
  const seconds = Math.max(0, Math.floor(elapsedMs / 1000))
  if (seconds < 5) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ago`
}

/** Pick the next tick interval based on elapsed-since-save. */
function tickInterval(elapsedMs: number): number {
  if (elapsedMs < ONE_MINUTE_MS) return TICK_FAST_MS
  if (elapsedMs < ONE_HOUR_MS) return TICK_MEDIUM_MS
  return TICK_SLOW_MS
}
